import { apiFetch } from "../../../shared/apiClient";
import {
  FACTSET_CHART_CREATOR_API,
  FACTSET_CHART_CREATOR_ERROR_CODES,
  FACTSET_CHART_CREATOR_RESPONSE_MAX_BYTES,
  FACTSET_ROUTES,
  REAUTH_REQUIRED_OAUTH_ERROR_CODES,
} from "../../../shared/factset/factsetConstants";
import type {
  FactsetChartParameters,
  FactsetChartPreview,
  FactsetDownloadExcelChart,
  FactsetGetStylesetsResult,
  FactsetStyleset,
} from "../../types";
import {
  buildDownloadExcelChartPayload,
  buildPreviewChartPayload,
  type PreviewChartPayloadOptions,
} from "../../chartCreatorPayload";
import { isAbortError } from "../../../shared/utils/abortError";

/** Client deadline for deterministic chart generation and workbook packaging. */
export const DEFAULT_CHART_CREATOR_TIMEOUT_MS = 5 * 60_000;

export class ChartCreatorReauthRequiredError extends Error {
  reauthorizationUrl: string;
  serverErrorCode: string;

  constructor(message: string, reauthorizationUrl: string, serverErrorCode: string) {
    super(message);
    this.name = "ChartCreatorReauthRequiredError";
    this.reauthorizationUrl = reauthorizationUrl;
    this.serverErrorCode = serverErrorCode;
    Object.setPrototypeOf(this, ChartCreatorReauthRequiredError.prototype);
  }
}

export class ChartCreatorFailedError extends Error {
  serverErrorCode?: string;
  httpStatus?: number;
  details?: unknown;

  constructor(
    message: string,
    options: { serverErrorCode?: string; httpStatus?: number; details?: unknown } = {},
  ) {
    super(message);
    this.name = "ChartCreatorFailedError";
    this.serverErrorCode = options.serverErrorCode;
    this.httpStatus = options.httpStatus;
    this.details = options.details;
    Object.setPrototypeOf(this, ChartCreatorFailedError.prototype);
  }
}

export class ChartCreatorTimeoutError extends Error {
  constructor(message = "FactSet Chart Creator request timed out.") {
    super(message);
    this.name = "ChartCreatorTimeoutError";
    Object.setPrototypeOf(this, ChartCreatorTimeoutError.prototype);
  }
}

export interface ChartCreatorRequestOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
}

interface ParsedApiError {
  error?: string;
  message?: string;
  details?: unknown;
  reauthorizationUrl?: string;
}

function abortError(message: string): DOMException | Error {
  if (typeof DOMException === "function") {
    return new DOMException(message, "AbortError");
  }
  const error = new Error(message);
  error.name = "AbortError";
  return error;
}

function withTimeoutSignal(
  signal: AbortSignal | undefined,
  timeoutMs: number,
): { signal: AbortSignal; cleanup: () => void; didTimeout: () => boolean } {
  const controller = new AbortController();
  let timedOut = false;
  const onAbort = () =>
    controller.abort((signal as AbortSignal & { reason?: unknown })?.reason);
  if (signal?.aborted) {
    controller.abort((signal as AbortSignal & { reason?: unknown }).reason);
  } else {
    signal?.addEventListener("abort", onAbort, { once: true });
  }
  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timeoutId);
      signal?.removeEventListener("abort", onAbort);
    },
    didTimeout: () => timedOut,
  };
}

async function readApiError(response: Response): Promise<ParsedApiError> {
  try {
    const body = await response.clone().json();
    if (body && typeof body === "object") return body as ParsedApiError;
  } catch {
    // Fall through to text.
  }
  try {
    const text = await response.text();
    return { message: text || `HTTP ${response.status}` };
  } catch {
    return { message: `HTTP ${response.status}` };
  }
}

async function throwForResponse(response: Response): Promise<never> {
  const body = await readApiError(response);
  const code = typeof body.error === "string" ? body.error : "";
  const message = body.message || `FactSet Chart Creator request failed (${response.status})`;

  if (REAUTH_REQUIRED_OAUTH_ERROR_CODES.has(code as never)) {
    throw new ChartCreatorReauthRequiredError(
      message,
      typeof body.reauthorizationUrl === "string" && body.reauthorizationUrl
        ? body.reauthorizationUrl
        : FACTSET_ROUTES.OAUTH_START,
      code,
    );
  }

  throw new ChartCreatorFailedError(message, {
    serverErrorCode: code || undefined,
    httpStatus: response.status,
    details: body.details,
  });
}

async function requestResponse<T>(
  path: string,
  init: RequestInit,
  options: ChartCreatorRequestOptions = {},
  readResponse: (response: Response) => Promise<T>,
): Promise<T> {
  const effectiveTimeoutMs = options.timeoutMs ?? DEFAULT_CHART_CREATOR_TIMEOUT_MS;
  const timeout = withTimeoutSignal(options.signal, effectiveTimeoutMs);
  try {
    const response = await apiFetch(path, { ...init, signal: timeout.signal });
    if (!response.ok) {
      await throwForResponse(response);
    }
    return await readResponse(response);
  } catch (error) {
    if (isAbortError(error)) {
      if (timeout.didTimeout()) {
        throw new ChartCreatorTimeoutError(
          `FactSet Chart Creator request timed out after ${effectiveTimeoutMs} ms.`,
        );
      }
      throw abortError("Chart Creator call cancelled by caller.");
    }
    throw error;
  } finally {
    timeout.cleanup();
  }
}

async function requestJson<T>(
  path: string,
  init: RequestInit,
  options: ChartCreatorRequestOptions = {},
): Promise<T> {
  return requestResponse(path, init, options, async (response) => {
    return (await response.json()) as T;
  });
}

function hasXlsxZipSignature(bytes: Uint8Array): boolean {
  return (
    bytes.length >= 4 &&
    bytes[0] === 0x50 &&
    bytes[1] === 0x4b &&
    bytes[2] === 0x03 &&
    bytes[3] === 0x04
  );
}

/** Chunked so a multi-megabyte workbook does not blow the argument limit. */
function bytesToBase64(bytes: Uint8Array): string {
  const chunkSize = 0x8000;
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const chunk = Array.from(bytes.subarray(offset, offset + chunkSize));
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

function normalizePngData(value: unknown): string | undefined {
  if (typeof value !== "string" || value.length === 0) return undefined;
  if (value.startsWith("data:")) return value;
  return `data:image/png;base64,${value}`;
}

function findPreviewNode(
  value: unknown,
  maxDepth: number = 6,
): Record<string, unknown> | undefined {
  if (maxDepth < 0 || !value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  const obj = value as Record<string, unknown>;
  if (typeof obj.fdsChartXml === "string") {
    return obj;
  }
  for (const child of Object.values(obj)) {
    const hit = findPreviewNode(child, maxDepth - 1);
    if (hit) return hit;
  }
  if (
    "fdsChartXml" in obj ||
    "error" in obj ||
    "pngData" in obj ||
    "pngDataUri" in obj ||
    "annotationsJson" in obj ||
    "wasStylesetChanged" in obj ||
    "message" in obj
  ) {
    return obj;
  }
  return undefined;
}

function normalizePreviewResponse(payload: unknown): FactsetChartPreview {
  const inner = findPreviewNode(payload) ?? {};
  const fdsChartXml = typeof inner.fdsChartXml === "string" ? inner.fdsChartXml : undefined;
  return {
    fdsChartXml,
    pngDataUri:
      typeof inner.pngDataUri === "string"
        ? inner.pngDataUri
        : normalizePngData(inner.pngData),
    annotationsJson:
      typeof inner.annotationsJson === "string" ? inner.annotationsJson : undefined,
    wasStylesetChanged:
      typeof inner.wasStylesetChanged === "boolean" ? inner.wasStylesetChanged : undefined,
    error:
      inner.error === null
        ? null
        : typeof inner.error === "string"
          ? inner.error
          : !fdsChartXml && typeof inner.message === "string"
            ? inner.message
            : undefined,
  };
}

function findStylesetsNode(
  value: unknown,
  maxDepth: number = 6,
): Record<string, unknown> | undefined {
  if (maxDepth < 0 || !value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  const obj = value as Record<string, unknown>;
  if (Array.isArray(obj.stylesets)) return obj;
  for (const child of Object.values(obj)) {
    const hit = findStylesetsNode(child, maxDepth - 1);
    if (hit) return hit;
  }
  return undefined;
}

function coerceStylesets(value: unknown): FactsetStyleset[] {
  if (!Array.isArray(value)) return [];
  const out: FactsetStyleset[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== "object") continue;
    const candidate = entry as Record<string, unknown>;
    if (typeof candidate.id !== "string" || typeof candidate.name !== "string") continue;
    out.push({ id: candidate.id, name: candidate.name });
  }
  return out;
}

function normalizeStylesetsResponse(payload: unknown): FactsetGetStylesetsResult {
  const inner = findStylesetsNode(payload) ?? {};
  return {
    stylesets: coerceStylesets(inner.stylesets),
    currentStylesetId:
      typeof inner.currentStylesetId === "string" ? inner.currentStylesetId : undefined,
  };
}

export async function previewChartViaApi(
  parameters: FactsetChartParameters,
  options: PreviewChartPayloadOptions & ChartCreatorRequestOptions = {},
): Promise<FactsetChartPreview> {
  const payload = buildPreviewChartPayload(parameters, options);
  const response = await requestJson<unknown>(
    FACTSET_ROUTES.CHART_CREATOR_PREVIEW,
    { method: "POST", body: JSON.stringify(payload) },
    options,
  );
  return normalizePreviewResponse(response);
}

export async function downloadExcelChartViaApi(
  parameters: FactsetChartParameters,
  options: ChartCreatorRequestOptions = {},
): Promise<FactsetDownloadExcelChart> {
  const payload = buildDownloadExcelChartPayload(parameters);
  return requestResponse(
    FACTSET_ROUTES.CHART_CREATOR_DOWNLOAD_EXCEL,
    { method: "POST", body: JSON.stringify(payload) },
    options,
    async (response) => {
      // Check the declared size before buffering: `arrayBuffer()` on a
      // runaway response allocates the whole thing inside the task pane.
      const declaredLength = Number(response.headers.get("content-length"));
      if (
        Number.isFinite(declaredLength) &&
        declaredLength > FACTSET_CHART_CREATOR_RESPONSE_MAX_BYTES
      ) {
        throw new ChartCreatorFailedError(
          `FactSet Chart Creator returned a workbook larger than the ${FACTSET_CHART_CREATOR_RESPONSE_MAX_BYTES}-byte limit.`,
          {
            serverErrorCode: FACTSET_CHART_CREATOR_ERROR_CODES.RESPONSE_TOO_LARGE,
            httpStatus: response.status,
          },
        );
      }
      const bytes = new Uint8Array(await response.arrayBuffer());
      if (bytes.length > FACTSET_CHART_CREATOR_RESPONSE_MAX_BYTES) {
        throw new ChartCreatorFailedError(
          `FactSet Chart Creator returned a workbook larger than the ${FACTSET_CHART_CREATOR_RESPONSE_MAX_BYTES}-byte limit.`,
          {
            serverErrorCode: FACTSET_CHART_CREATOR_ERROR_CODES.RESPONSE_TOO_LARGE,
            httpStatus: response.status,
          },
        );
      }
      if (!hasXlsxZipSignature(bytes)) {
        throw new ChartCreatorFailedError(
          "FactSet Chart Creator returned an invalid or empty Excel workbook.",
          {
            serverErrorCode: FACTSET_CHART_CREATOR_ERROR_CODES.INVALID_RESPONSE,
            httpStatus: response.status,
          },
        );
      }
      return {
        workbookBase64: bytesToBase64(bytes),
        contentType:
          response.headers.get("content-type") || FACTSET_CHART_CREATOR_API.XLSX_CONTENT_TYPE,
        status: response.status,
        error: null,
      };
    },
  );
}

export async function getStylesetsViaApi(
  options: ChartCreatorRequestOptions = {},
): Promise<FactsetGetStylesetsResult> {
  const response = await requestJson<unknown>(
    FACTSET_ROUTES.CHART_CREATOR_STYLESETS,
    { method: "GET" },
    options,
  );
  return normalizeStylesetsResponse(response);
}
